import { type PickerProps } from 'antd5/es/date-picker/generatePicker';
import moment, { type Moment } from 'moment';
import React, { useCallback } from 'react';

import { DatePicker } from 'components/ui';
import { forceMomentToUTC } from 'utils/date/forceMomentToUTC';

type DatePickerProps = PickerProps<Moment> & {
  format: string;
};

/***
 * DatePicker component that wraps the antd DatePicker component and forces the onChange prop to use a UTC date
 * to avoid weird timezone issues.
 *
 * Explanation:
 *
 * If there is not an initial value, the antd component will return a date in the local timezone however if there is and the format
 * does not include a timezone, it will return a date in UTC. So depending on the situation and timing, you can end up with picking
 * one date in the picker and then submitting a different date.
 *
 * ```typescript
 * const thing1 = moment('2023-08-10T00:00:00.000+02:00');
 * const thing2 = moment('2023-08-10T00:00:00.000+02:00', 'YYYY-MM-DD');
 * console.log(thing1.format('YYYY-MM-DD')); // 2023-08-09
 * console.log(thing2.format('YYYY-MM-DD')); // 2023-08-10
 * ```
 *
 * This component fixes that by always forcing the date to be in UTC, so the user
 * will always get the same date back that they picked.
 *
 * @see https://app.shortcut.com/skyspecs/story/65045/odd-datepicker-behavior
 *
 * @constructor
 */
export const UTCDatePicker: React.FunctionComponent<any> = ({
  onChange,
  format = 'YYYY-MM-DD',
  ...props
}: DatePickerProps) => {
  const handleChange = useCallback(
    (date: moment.Moment | null, str: string) => {
      if (moment.isMoment(date)) {
        const newDate = forceMomentToUTC(date);
        onChange?.(newDate, newDate.format(format));
        return;
      }

      onChange?.(date, str);
    },
    [onChange, format]
  );

  return <DatePicker format={format} onChange={handleChange} {...props} />;
};
